import React, { useEffect, useMemo, useState } from "react";
import styles from "./card.module.scss";
import { IBucketListItem } from "api/entities/bucket";
import { useApi } from "utils/hooks/use-api";
import { bucketService } from "api/services/bucket-service";
import { IEntityMetricsResponse } from "api/entities/metrics";
import {
  CheckCircleOutline,
  CloudOff,
  DataArrayOutlined,
  DataObjectOutlined,
  HighlightOffOutlined,
  LockOutlined,
  StorageOutlined,
} from "@mui/icons-material";
import { IShardClusterDetailsItem } from "api/entities/shard-cluster";
import { IIdName } from "api/entities/id-name";
import { shardRepositoryService } from "api/services/shard-repository-service";
import { Formatters } from "helper/formatters";
import Skeleton from "@mui/material/Skeleton";
import DataDisplay, { IDataDisplayStatus } from "../data-display/data-display";
import UsageChart from "../usage-chart/usage-chart";

interface ICardProps {
  bucket: IBucketListItem;
}

function Card(props: ICardProps) {
  const [api, isLoading] = useApi();
  const [metrics, setMetrics] = useState<IEntityMetricsResponse>(null);
  const [nodeMetrics, setNodeMetrics] =
    useState<Map<string, IEntityMetricsResponse>>(null);
  const [storagePool, setStoragePool] =
    useState<IShardClusterDetailsItem>(null);

  const getSuccessNodeCount = (pool: IIdName<string>[]): number => {
    let count = 0;
    pool?.forEach((node) => {
      if (nodeMetrics?.get(node.id)?.status === "up") count++;
    });
    return count;
  };

  const getStatus = (up: number, redundancy: number): IDataDisplayStatus => {
    if (metrics?.status === "down" || isLoading)
      return { color: "Inherit", text: "N/A" };

    const percentage = (up / redundancy) * 100;

    if (percentage <= 99 && percentage >= 51) {
      return { color: "orange", text: "Degraded" };
    } else if (percentage <= 50) {
      return { color: "red", text: "Critical" };
    } else {
      return { color: "green", text: "Optimal" };
    }
  };

  const contentNodeSuccess = useMemo(() => {
    return getSuccessNodeCount(storagePool?.contentStorageNodes);
    // eslint-disable-next-line
  }, [storagePool, nodeMetrics]);

  const metaNodeSuccess = useMemo(() => {
    return getSuccessNodeCount(storagePool?.metaStorageNodes);
    // eslint-disable-next-line
  }, [storagePool, nodeMetrics]);

  const keyNodeSuccess = useMemo(() => {
    return getSuccessNodeCount(storagePool?.keyStorageNodes);
    // eslint-disable-next-line
  }, [storagePool, nodeMetrics]);

  const contentNodeStatus = useMemo(() => {
    return getStatus(contentNodeSuccess, storagePool?.contentRedundancyLevel);
    // eslint-disable-next-line
  }, [storagePool, contentNodeSuccess, metrics, isLoading]);

  const metaNodeStatus = useMemo(() => {
    return getStatus(metaNodeSuccess, storagePool?.metaRedundancyLevel);
    // eslint-disable-next-line
  }, [storagePool, metaNodeSuccess, metrics, isLoading]);

  const keyNodeStatus = useMemo(() => {
    return getStatus(keyNodeSuccess, storagePool?.keyRedundancyLevel);
    // eslint-disable-next-line
  }, [storagePool, keyNodeSuccess, metrics, isLoading]);

  useEffect(() => {
    api(bucketService.getMetrics(props.bucket.id)).then((metrics) => {
      if (!!metrics && metrics.length > 0) {
        setMetrics(metrics[0]);
      }
      // api(bucketService.get(props.bucket.id)).then(({ storagePool }) => {
      //   setStoragePool(storagePool);
      //   const storageNodes = [
      //     ...storagePool.contentStorageNodes,
      //     ...storagePool.metaStorageNodes,
      //     ...storagePool.keyStorageNodes,
      //   ];

      //   const uniqueStorageNodeIds = Array.from(
      //     new Map(storageNodes.map((item) => [item.id, item.id])).values()
      //   );
      //   api(shardRepositoryService.getMetrics(uniqueStorageNodeIds)).then(
      //     (metrics) => {
      //       setNodeMetrics(
      //         new Map(metrics.map((metric) => [metric.id, metric]))
      //       );
      //     }
      //   );
      // });
    });
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.title}>{props.bucket.name}</div>
          <div>
            {isLoading ? (
              <span>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ width: "70px" }}
                />
              </span>
            ) : metrics?.status === "up" ? (
              <div className={styles.online}>
                <CheckCircleOutline />
                <span>Online</span>
              </div>
            ) : (
              <div className={styles.offline}>
                <HighlightOffOutlined />
                <span>Offline</span>
              </div>
            )}
          </div>
        </div>
        <div className={styles.progressTitle}>Security</div>
        <div className={styles.dataDisplayContainer}>
          <DataDisplay
            title="Immutability & Encryption"
            icon={<LockOutlined fontSize="small" />}
            value={<CheckCircleOutline fontSize="small" />}
            highlight={metrics?.status === "up"}
          />
          <DataDisplay
            title="Air Gap"
            icon={<CloudOff fontSize="small" />}
            value={<CheckCircleOutline fontSize="small" />}
            highlight={metrics?.status === "up"}
          />
        </div>
        <div className={styles.progressTitle}>Resiliency</div>
        <div className={styles.dataDisplayContainer}>
          <DataDisplay
            title="Content Shard Repos"
            color={contentNodeStatus.color}
            icon={<StorageOutlined fontSize="small" />}
            value={
              isLoading ? (
                <span>
                  <Skeleton animation="wave" variant="text" />
                </span>
              ) : (
                <span>{contentNodeStatus.text}</span>
              )
            }
          />
          <DataDisplay
            title="Key Shard Repos"
            color={keyNodeStatus.color}
            icon={<StorageOutlined fontSize="small" />}
            value={
              isLoading ? (
                <span>
                  <Skeleton animation="wave" variant="text" />
                </span>
              ) : (
                <span>{keyNodeStatus.text}</span>
              )
            }
          />
          <DataDisplay
            title="Metadata Shard Repos"
            color={metaNodeStatus.color}
            icon={<StorageOutlined fontSize="small" />}
            value={
              isLoading ? (
                <span>
                  <Skeleton animation="wave" variant="text" />
                </span>
              ) : (
                <span>{metaNodeStatus.text}</span>
              )
            }
          />
        </div>
        <div className={styles.progressTitle}>Usage</div>
        <div className={styles.dataDisplayContainer}>
          <DataDisplay
            title="Size"
            icon={<DataObjectOutlined fontSize="small" />}
            value={
              isLoading ? (
                <span>
                  <Skeleton animation="wave" variant="text" />
                </span>
              ) : (
                <span>
                  {metrics?.sizeBytes
                    ? Formatters.formatStorageSpace(metrics?.sizeBytes)
                    : "N/A"}
                </span>
              )
            }
          />
          <DataDisplay
            title="Objects"
            icon={<DataArrayOutlined fontSize="small" />}
            value={
              isLoading ? (
                <span>
                  <Skeleton animation="wave" variant="text" />
                </span>
              ) : (
                <span>
                  {metrics?.objectNumber ? metrics?.objectNumber : "N/A"}
                </span>
              )
            }
          />
        </div>
        <div className={styles.chartContainer}>
          <UsageChart id={props.bucket.id} />
        </div>
      </div>
    </>
  );
}

export default Card;
